import utils from "../../../utils/utils.js";

let timer;
let sessionTimer;
let notificationTimer;
// Incremented whenever the session is torn down (logout / clearLocalStorage).
// An in-flight autoLogin compares it after its awaits and drops a stale result.
let sessionGeneration = 0;
// Early-logout diagnosis: when the refresh timer was expected to fire, to measure how late it ran
// (sleeping laptop / throttled background tab).
let refreshDueAt = null;
let lastRefreshDelaySeconds = null;

const LOGOUT_DIAGNOSTICS_KEY = "logoutDiagnostics";

function scheduleAutoLogin(context, delay) {
    refreshDueAt = new Date().getTime() + delay;

    timer = setTimeout(function() {
        context.dispatch("autoLogin");
    }, delay);
}

// Keeps why the session ended in localStorage (it survives the logout cleanup);
// it is sent to the API after the next successful login.
function recordLogoutDiagnostic(details) {
    try {
        const now = new Date().getTime();
        const secondsUntil = key => {
            const value = +localStorage.getItem(key);

            return value ? Math.round((value - now) / 1000) : null;
        };

        const entry = {
            ...details,
            at: new Date(now).toISOString(),
            page: window.location.pathname,
            visibility: document.visibilityState,
            online: navigator.onLine,
            tokenExpiresInSeconds: secondsUntil("tokenExpiration"),
            sessionExpiresInSeconds: secondsUntil("refreshTokenExpiration"),
            lastRefreshDelaySeconds: lastRefreshDelaySeconds,
            refreshOverdueSeconds: refreshDueAt ? Math.round((now - refreshDueAt) / 1000) : null,
            hasRefreshToken: !!localStorage.getItem("refreshToken")
        };

        const list = JSON.parse(localStorage.getItem(LOGOUT_DIAGNOSTICS_KEY) || "[]");

        list.push(entry);
        localStorage.setItem(LOGOUT_DIAGNOSTICS_KEY, JSON.stringify(list.slice(-5)));
    } catch {
        // Diagnosis must never block the logout itself.
    }
}

export default {
    async login(context, payload) {
        const responseData = await utils.utility.fetch("api/auth/authorize", "POST", null, { username: payload.username, password: payload.password, captchaId: payload.captchaId, captchaText: payload.captchaText });

        if (responseData) {
            // A fresh credential login must never continue on top of a leftover session,
            // otherwise the guard redirects /validate-otp to /home and OTP is skipped.
            context.dispatch("clearLocalStorage");

            if (responseData.data.passwordExpired) {
                context.commit("forceResetPassword", { username: payload.username });
            } else {
                context.commit("setTwoFactorAuth", { ...responseData.data, username: payload.username });
            }
        }
    },
    async autoLogin(context) {
        const generation = sessionGeneration;
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshDueAt) lastRefreshDelaySeconds = Math.round((new Date().getTime() - refreshDueAt) / 1000);

        if (!refreshToken) {
            context.dispatch("autoLogout", { reason: "refresh-no-token" });

            return;
        }

        let response = null;

        try {
            response = await fetch(`${process.env.VUE_APP_SNIFFER_API}/api/auth/refreshtoken`, {
                method: "POST",
                headers: new Headers({
                    "Content-Type": "application/json"
                }),
                body: JSON.stringify({
                    refreshToken: refreshToken
                })
            });
        } catch (networkError) {
            if (generation !== sessionGeneration) return;

            context.dispatch("autoLogout", { reason: "refresh-network-error", error: networkError && networkError.message });

            return;
        }

        const responseData = await response.json();

        if (generation !== sessionGeneration) return;

        if (!response.ok) {
            context.dispatch("autoLogout", { reason: "refresh-rejected", status: response.status, serverMessage: responseData && (responseData.detail || responseData.message) });

            return;
        }

        const tokenExpiresIn = 28 * 60 * 1000;
        const expirationTime = new Date().getTime() + tokenExpiresIn;
        
        localStorage.setItem("token", responseData.data.token);
        localStorage.setItem("tokenExpiration", expirationTime);
        localStorage.setItem("refreshToken", responseData.data.refreshToken);

        scheduleAutoLogin(context, tokenExpiresIn);

        context.commit("setUser", {
            ...responseData.data,
            expiresIn: expirationTime,
            refreshToken: responseData.data.refreshToken,
            refreshExpiresIn: localStorage.getItem("refreshTokenExpiration")
        });

        const isSessionEnding = context.rootGetters.isSessionEnding;

        if (isSessionEnding) context.dispatch("timeoutWarning");

        context.commit("clearPasswordPermission");

        const isInternalUser = utils.utility.isInternalUser(context.rootGetters.username);

        await context.dispatch("fetchClientContext", { workspaceId: context.getters.workspaceIdForHeader });

        if (!isInternalUser) await utils.utility.fetch("api/auth/updatepasswordchangepermission", "PUT", null, { username: context.rootGetters.username, permissionStatus: false }, true);
    },
    async auth(context, payload) {
        const responseData = await utils.utility.fetch("api/auth/token", "POST", null, { code: payload.code, otp: payload.otp })
                
        const tokenExpiresIn = +responseData.data.tokenExpireInMinutes * 60 * 1000;
        const expirationTime = new Date().getTime() + tokenExpiresIn;

        const refreshExpiresIn = +responseData.data.refreshTokenExpireInMinutes * 60 * 1000;
        const refreshExpirationTime = new Date().getTime() + refreshExpiresIn;       
        
        localStorage.setItem("token", responseData.data.token);
        localStorage.setItem("tokenExpiration", expirationTime);
        localStorage.setItem("refreshToken", responseData.data.refreshToken);
        localStorage.setItem("refreshTokenExpiration", refreshExpirationTime);

        scheduleAutoLogin(context, tokenExpiresIn);

        sessionTimer = setTimeout(function() {
            context.dispatch("sessionTimeout");
        }, (refreshExpiresIn - 540000));

        context.commit("setUser", { ...responseData.data, expiresIn: expirationTime, refreshExpiresIn: refreshExpirationTime });
        context.commit("clearPasswordPermission");

        const isInternalUser = utils.utility.isInternalUser(context.rootGetters.username);

        await context.dispatch("fetchClientContext");

        context.dispatch("sendLogoutDiagnostics");
        
        if (!isInternalUser) await utils.utility.fetch("api/auth/updatepasswordchangepermission", "PUT", null, { username: context.rootGetters.username, permissionStatus: false }, true);
    },
    logout(context) {
        //utils.utility.fetch("api/auth/logout", "PUT", null, null, true);
        const token = context.rootGetters.token;
        const workspaceId = context.rootGetters.workspaceIdForHeader;

        // The backend logout does not unlock feeds, and OperationPage unmounts only after the token is cleared below,
        // so unlock here while the request can still carry the token.
        if (token && utils.utility.isGrantedPermission("Engagement")) context.dispatch("releaseLockedFeeds", true);

        try {
            const headers = new Headers();
            headers.append("Authorization", `Bearer ${token}`);
            headers.append("WorkspaceId", workspaceId);

            fetch(`${process.env.VUE_APP_SNIFFER_API}/api/auth/logout`, {
                method: "PUT",
                headers: headers
            });
        } finally {
            sessionGeneration++;

            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("tokenExpiration");
            localStorage.removeItem("refreshTokenExpiration");

            clearTimeout(timer);
            clearTimeout(sessionTimer);
            clearTimeout(notificationTimer);

            context.commit("setTwoFactorAuth", {
                code: null
            });

            context.commit("setUser", {
                token: null,
                expiresIn: null,
                refreshToken: null,
                refreshExpiresIn: null
            });

            context.commit("clearUserClientContext");
            context.commit("setSessionEnd", { sessionEnding: false });
            context.commit("fireSessionTimeoutNotification", { showNotification: false });
        }
    },
   async tryLogin(context) {
        const token = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");
        const tokenExpiration = localStorage.getItem("tokenExpiration");
        const refreshTokenExpiration = localStorage.getItem("refreshTokenExpiration");

        const expiresIn = +tokenExpiration - new Date().getTime();
        const refreshExpiresIn = +refreshTokenExpiration - new Date().getTime();
        
        if (token && refreshToken && refreshExpiresIn < 15000) return;

        if (token && refreshToken) {
            scheduleAutoLogin(context, expiresIn);
    
            sessionTimer = setTimeout(function() {
                context.dispatch("sessionTimeout");
            }, (refreshExpiresIn - 540000));

            context.commit("setUser", {
                token: token,
                refreshToken: refreshToken,
                expiresIn: tokenExpiration,
                refreshExpiresIn: refreshTokenExpiration
            });

            await context.dispatch("fetchClientContext", { workspaceId: context.getters.workspaceIdForHeader });

            const canUpdatePassword = context.rootGetters.isPasswordResetProcess;

            const isInternalUser = utils.utility.isInternalUser(context.rootGetters.username);

            if (canUpdatePassword && !isInternalUser) {
                context.commit("clearPasswordPermission");
                
                await utils.utility.fetch("api/auth/updatepasswordchangepermission", "PUT", null, { username: context.rootGetters.username, permissionStatus: false }, true);
            }
        }
    },
    autoLogout(context, payload) {
        recordLogoutDiagnostic(payload || { reason: "unknown" });

        context.dispatch("logout");

        context.commit("setAutoLogout");
    },
    sendLogoutDiagnostics(context) {
        let list = [];

        try {
            list = JSON.parse(localStorage.getItem(LOGOUT_DIAGNOSTICS_KEY) || "[]");

            localStorage.removeItem(LOGOUT_DIAGNOSTICS_KEY);
        } catch {
            return;
        }

        for (const item of list) {
            context.dispatch("logClientSideError", { traceId: "AUTH-DIAG", message: JSON.stringify(item) }, { root: true });
        }
    },
    sessionTimeout(context) {
        context.commit("setSessionEnd", { sessionEnding: true });
    },
    timeoutWarning(context) {
        notificationTimer = setTimeout(function() {
            const tokenExpiration = context.rootGetters.tokenExpiration;
            const expiresIn = +tokenExpiration - new Date().getTime();
            const remainingSeconds = Math.floor(expiresIn / 1000);

            context.commit("fireSessionTimeoutNotification", { showNotification: true, remainingSeconds: remainingSeconds });
        }, 360000);
    },
    async fetchNewOtpCode(_, payload) {
        await utils.utility.fetch("api/auth/resendotp", "POST", null, { username: payload });
    },
    clearLocalStorage(context) {
        sessionGeneration++;

        clearTimeout(timer);
        clearTimeout(sessionTimer);
        clearTimeout(notificationTimer);

        context.commit("clearUser");

        const token = localStorage.getItem("token");

        if (token) localStorage.removeItem("token");

        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken) localStorage.removeItem("refreshToken");

        const tokenExpiration = localStorage.getItem("tokenExpiration");

        if (tokenExpiration) localStorage.removeItem("tokenExpiration");

        const refreshTokenExpiration = localStorage.getItem("refreshTokenExpiration");

        if (refreshTokenExpiration) localStorage.removeItem("refreshTokenExpiration");
    },
    clearTwoFactorAuthDetails(context) {
        context.commit("clearTwoFactorAuth");
    },
    putLanguage(context, payload) {
        //POST server..

        context.commit("putLangugage", payload);
    },
    async checkExpiredAccounts(context) {
        const responseData = await utils.utility.fetch("api/editor/getexpiredaccountscount", "GET", null, null, true);

        context.commit("checkExpiredAccounts", responseData);
    },
    async fetchExpiredAccounts(context) {
        const responseData = await utils.utility.fetch("api/editor/getexpiredaccounts", "GET", null, null);
        
        context.commit("setExpiredAccounts", responseData);
    },
    async putOperationStatus(context, payload) {
        const status = payload.status;

        if (status == "passive") {
            await utils.utility.fetch("api/editor/unLockfeeds", "PUT", null, null);
            await utils.utility.fetch("api/editor/setuseroperationpassive", "PUT", null, null);
        }
        else if (status == "active") {
            await utils.utility.fetch("api/editor/setuseroperationactive", "PUT", null, null);
        }

        context.commit("putOperationStatus", status);
    },
    releaseLockedFeeds(context, payload) {
        // Best-effort cleanup, often sent while the page is closing: skip it once the session is gone
        // and never surface its failure.
        if (!context.rootGetters.token) return;

        utils.utility.fetch("api/editor/unLockfeeds", "PUT", null, null, true, payload).catch(() => {});
    },
    async checkLastestOperationTime(context) {
        const queueStats = context.rootState.queue.stats;
        const queueId = context.state.user.operationQueueId;
        const feedQueueStats = { queueCount: 0, userQueueCount: 0 };
        
        if (Object.keys(queueStats).length != 0) {
            feedQueueStats.queueCount = queueStats.queueCount;
            feedQueueStats.userQueueCount = queueStats.userQueueCount;
        }
        
        const responseData = await utils.utility.fetch(`api/editor/checkuserlogoff?queuecount=${feedQueueStats.queueCount}&userqueuecount=${feedQueueStats.userQueueCount}&queueId=${queueId}`, "GET", null, null, true);
        
        if (responseData.data.isUserLogOff) context.dispatch("releaseLockedFeeds", false);

        context.commit("checkLastestOperationTime", responseData);
    },
    async fetchClientContext(context, payload) {
        let url = "";

        if (payload && payload.workspaceId) {
            url = `api/Workspace/GetCurrentContext?workspaceId=${payload.workspaceId}`;
        } else {
            url = "api/Workspace/GetCurrentContext";
        }

        const responseData = await utils.utility.fetch(url, "GET", null, null, true);

        if (responseData) {
            context.commit("setUserClientContext", responseData.data);
        }
    },
    async checkClientContext(context) {
        if(!context.getters.workspaceId){
            const responseData = await utils.utility.fetch("api/workspace/getcurrentcontext", "GET", null, null, true);
            
            if(responseData) context.commit("setUserClientContext", responseData.data);
        }
    },
    async fetchUserContactInfo() {        
        const responseData = await utils.utility.fetch(`api/auth/getuserinfo`, "GET", null, null, true);
            
        return responseData.data;
    },
    async updateProfile(context, payload) {
        const updateUserInfoPayload = {
            Email: payload.email,
            Phone: payload.phone,
            VerificationSelect: payload.verificationMethod === "email"
        };
        
        const responseData = await utils.utility.fetch("api/Auth/UpdateUserInfo", "POST", null, updateUserInfoPayload);
        
        if (responseData) context.commit("updateUserProfile", payload);
        
        return responseData;
    },
    async putPasswordPermission(context) {
        await utils.utility.fetch("api/auth/updatepasswordchangepermission", "PUT", null, { username: context.rootGetters.username, permissionStatus: true }, true);
        const responseData = await utils.utility.fetch("api/auth/getpasswordchangepermission", "GET", null, null, true);

        context.commit("setPasswordResetPermission", responseData.data);
        context.commit("setPasswordResetUser", context.rootGetters.username);
    },
    async putPassword(context, payload) {
        const responseData = await utils.utility.fetch("api/auth/updatepassword", "PUT", null, payload);

        if (responseData == "") {
            context.dispatch("logout");
            context.dispatch("putPasswordChangeStatus", true);
            context.commit("clearPasswordPermission");
        }
    },
    async fetchPasswordProcessStatus(context) {
        const responseData = await utils.utility.fetch("api/auth/getpasswordchangepermission", "GET", null, null, true);

        context.commit("setPasswordResetPermission", responseData.data);
    },
    putPasswordChangeStatus(context, payload) {
        context.commit("setPasswordChangeStatus", payload);
    },
    clearPasswordPermission(context) {
        context.commit("clearPasswordPermission");
    },
    putUserIdleStatus(context, payload) {
        context.commit("setUserIdleStatus", payload);
    },
    putOperationQueue(context, payload) {
        context.commit("setOperationQueue", payload);
    }
}